// AppShell — Utility Desk pattern: dark navy topbar with horizontal
// nav, no left sidebar. Brand + nav items on the left; search, alerts bell,
// settings gear, and user menu on the right.
//
// Pattern lifted from UtilityDesk/src/components/TopBar.tsx
// (h-[60px] dark navy bar, horizontal NavItem buttons, white-alpha hover states).

import { type ReactNode, useCallback, useRef, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Boxes,
  CalendarDays,
  Settings,
  CheckCircle2,
  ClipboardCheck,
  TrendingUp,
  Clock,
} from 'lucide-react';
import {
  getMe,
  getDashboardToday,
  getCustomers,
  getJobs,
  getInventorySkus,
  getSkuCategories,
  getCrews,
  getCrewUtilization,
} from '../api/client.js';
import { currentWeekRange, currentMonthRange } from '../lib/hours-range.js';
import { cn } from '../lib/utils.js';
import { NotificationsBell } from './NotificationsBell.js';
import { UserMenu } from './UserMenu.js';
import { PillNav, type PillNavTab } from './PillNav.js';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog.js';
import { usePollActive } from '../hooks/usePollActive.js';

const baseNavItems: PillNavTab[] = [
  { to: '/',           label: 'Today',     icon: LayoutDashboard, end: true },
  { to: '/customers',  label: 'Customers', icon: Users },
  { to: '/jobs',       label: 'Jobs',      icon: Briefcase },
  { to: '/inventory',  label: 'Inventory', icon: Boxes },
  { to: '/calendar',   label: 'Calendar',  icon: CalendarDays },
  // Crew Utilization reporting — office-wide; no role gate in nav.
  { to: '/hours',      label: 'Crew Utilization', icon: Clock },
];

interface AppShellProps {
  children: ReactNode;
}

const prefetchMap: Record<string, Array<{ queryKey: string[]; queryFn: () => Promise<unknown> }>> = {
  '/':          [{ queryKey: ['dashboard', 'today'], queryFn: getDashboardToday }],
  '/customers': [{ queryKey: ['customers'], queryFn: getCustomers }],
  '/jobs':      [{ queryKey: ['jobs'], queryFn: getJobs }],
  '/inventory': [{ queryKey: ['inventory', 'skus'], queryFn: () => getInventorySkus() }],
  '/calendar':  [{ queryKey: ['crews'], queryFn: getCrews }],
  '/settings':  [{ queryKey: ['sku-categories'], queryFn: getSkuCategories }],
  // Crew Utilization defaults to week + month ranges — prefetch the exact
  // period key so the page's useQuery hits a warm cache. Helpers are pure/local,
  // matching what HoursPage computes on mount (the chart series is year-driven,
  // not prefetched here).
  '/hours':     (() => {
    const week = currentWeekRange();
    const month = currentMonthRange();
    return [{
      queryKey: ['crew-utilization', 'period', week.from, week.to, month.from, month.to],
      queryFn: () => getCrewUtilization({
        weekFrom: week.from, weekTo: week.to, monthFrom: month.from, monthTo: month.to,
      }),
    }];
  })(),
};

export function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handlePrefetch = useCallback(
    (to: string) => {
      const queries = prefetchMap[to];
      if (!queries) return;
      for (const q of queries) {
        queryClient.prefetchQuery({ queryKey: q.queryKey, queryFn: q.queryFn, staleTime: 10_000 });
      }
    },
    [queryClient],
  );

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe });

  // Gate the topbar dashboard poll to office hours (Mon–Fri 07:00–17:00 ET).
  const pollActive = usePollActive();
  const { data: dashboard } = useQuery({
    queryKey: ['dashboard', 'today'],
    queryFn: getDashboardToday,
    refetchInterval: pollActive ? 30_000 : false,
    staleTime: 10_000,
  });

  const crewActivity = dashboard?.crewActivity ?? [];
  const shownActivityRef = useRef<Set<string>>(new Set());
  const [activityQueue, setActivityQueue] = useState<
    Array<{
      kind: 'estimate_submitted' | 'install_completed';
      jobId: string;
      displayName: string;
      customerName: string;
      crewMemberName: string;
      timestamp: string;
    }>
  >([]);
  const activityInitializedRef = useRef(false);

  useEffect(() => {
    if (!dashboard) return;
    if (!activityInitializedRef.current) {
      for (const evt of crewActivity) {
        shownActivityRef.current.add(`${evt.kind}|${evt.jobId}|${evt.timestamp}`);
      }
      activityInitializedRef.current = true;
      return;
    }
    const newEvents = crewActivity.filter((evt) => {
      const key = `${evt.kind}|${evt.jobId}|${evt.timestamp}`;
      if (shownActivityRef.current.has(key)) return false;
      shownActivityRef.current.add(key);
      return true;
    });
    if (newEvents.length > 0) {
      setActivityQueue((q) => [...q, ...newEvents]);
    }
  }, [dashboard, crewActivity]);

  const currentActivity = activityQueue[0] ?? null;
  const dismissActivity = useCallback(() => {
    setActivityQueue((q) => q.slice(1));
  }, []);

  return (
    <div className="flex h-screen w-full flex-col bg-background">
      {/* ── TOPBAR (full-width, dark navy — Utility Desk pattern) ─────────── */}
      <header className="flex h-[60px] shrink-0 items-center gap-10 border-b border-white/10 bg-sidebar pl-4 pr-5">
        {/* Brand */}
        <div className="flex items-center gap-2.5 shrink-0">
          <Boxes size={20} className="shrink-0 text-white" />
          <span className="text-[15px] font-semibold tracking-tight text-white whitespace-nowrap leading-none">
            Inventory & Crew Management
          </span>
        </div>

        {/* Page nav — PillNav with sliding indicator (ported from Akrites) */}
        <div className="flex items-center min-w-0">
          <PillNav
            tabs={[
              ...baseNavItems,
              ...(me?.role === 'owner' || me?.role === 'admin' || me?.role === 'back_office'
                ? [{ to: '/financials', label: 'Financials', icon: TrendingUp } as PillNavTab]
                : []),
            ]}
            onPrefetch={handlePrefetch}
          />
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right: bell + settings + user */}
        <div className="flex items-center gap-2 shrink-0">
          {dashboard && (
            <NotificationsBell
              lowInventory={dashboard.lowInventory}
              lowInventoryTotal={dashboard.lowInventoryTotal}
              awaitingPayments={dashboard.awaitingPayments}
            />
          )}

          <NavLink
            to="/settings"
            onMouseEnter={() => handlePrefetch('/settings')}
            title="Settings"
            className={({ isActive }) =>
              cn(
                'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                isActive
                  ? 'bg-white/20 text-white'
                  : 'text-white/70 hover:bg-white/15 hover:text-white',
              )
            }
          >
            <Settings className="h-4 w-4" />
          </NavLink>

          {/* Admin link — visible only to admins. Goes to /admin/users
              (or whichever default we route /admin to). */}
          {me?.role === 'admin' && (
            <NavLink
              to="/admin/users"
              title="Admin"
              className={({ isActive }) =>
                cn(
                  'rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wider transition-colors',
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/15 hover:text-white',
                )
              }
            >
              Admin
            </NavLink>
          )}

          {me && <UserMenu me={me} />}
        </div>
      </header>

      {/* ── MAIN CONTENT (full-width, no sidebar) ─────────────────────────── */}
      <main className="flex-1 overflow-auto px-8 pb-6">{children}</main>

      {/* Crew-activity modal overlay — click anywhere or Escape to dismiss.
          Built on the shared ui/dialog (Radix) primitive: it supplies the
          focus-trap, scrim, Escape + click-outside dismissal (routed through
          onOpenChange → dismissActivity). hideClose keeps the original no-X
          look, matching the "Click anywhere or press Esc" copy below. */}
      {currentActivity && (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) dismissActivity();
          }}
        >
          <DialogContent
            hideClose
            className="block min-w-[340px] max-w-[calc(100vw-2rem)] px-10 py-8 text-center"
          >
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              {currentActivity.kind === 'install_completed' ? (
                <CheckCircle2 className="h-6 w-6 text-primary" />
              ) : (
                <ClipboardCheck className="h-6 w-6 text-primary" />
              )}
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground">
              {currentActivity.kind === 'install_completed'
                ? 'Install Complete'
                : 'Estimate Submitted'}
            </DialogTitle>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Crew</span>
                <span className="font-medium text-foreground">{currentActivity.crewMemberName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Customer</span>
                <span className="font-medium text-foreground">{currentActivity.customerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Job</span>
                <button
                  className="font-medium text-primary underline-offset-2 hover:underline"
                  onClick={() => {
                    dismissActivity();
                    navigate(`/jobs/${currentActivity.jobId}`);
                  }}
                >
                  {currentActivity.displayName}
                </button>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Time</span>
                <span className="text-foreground">
                  {new Date(currentActivity.timestamp).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
            <p className="mt-5 text-xs text-taupe">Click anywhere or press Esc to dismiss</p>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
